<template>
  <div id="subscription-type-edit">
    <overlay-loading-progress
      :LoadingFlag="OverlayLoadingFlag"
    ></overlay-loading-progress>
    <v-card elevation="8" class="rounded-xl st-card">
      <!-- Header -->
      <v-card-title
        class="page-header-gradient white--text d-flex align-center pa-5"
      >
        <v-icon left large color="white" class="mr-3">mdi-pencil-box</v-icon>
        <div>
          <h2 class="text-h5 font-weight-bold mb-0">{{ pageTitle }}</h2>
          <div class="text-caption st-header-sub">{{ pageDescription }}</div>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closePrompt" large>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-5">
        <!-- Loading Indicator -->
        <div class="d-flex justify-center mb-3" v-if="LoadingFlag">
          <b-spinner
            variant="primary"
            type="grow"
            label="Loading..."
          ></b-spinner>
        </div>

        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="confirmAlert"
        >
          <v-row>
            <!-- LEFT: Form sections -->
            <v-col cols="12" md="8">
              <p class="text-caption mb-3">
                <span class="text-danger">*</span> indicates required field.
                Instructions under each section change as you fill the form.
              </p>

              <!-- 1. Basic details -->
              <v-card outlined class="st-section mb-4">
                <div class="st-section-title">
                  <v-avatar size="26" color="#0a79df" class="mr-2 white--text">1</v-avatar>
                  Basic details
                </div>
                <v-row class="px-4 pt-2">
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">
                      <span class="text-danger">*</span> Year
                    </h6>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      :items="JciYearCodeOptions"
                      :rules="JciYearCodeRules"
                      :loading="JciYearCodeOptionsLoading"
                      v-model="JciYearCode"
                      placeholder="Select Year"
                      dense
                      clearable
                      required
                      outlined
                      prepend-inner-icon="mdi-calendar"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">
                      <span class="text-danger">*</span> Contribution Type
                    </h6>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      :items="ContributionTypeIdOptions"
                      :rules="ContributionTypeIdRules"
                      :loading="ContributionTypeIdOptionsLoading"
                      v-model="ContributionTypeId"
                      placeholder="Select Contribution Type"
                      dense
                      clearable
                      required
                      outlined
                      prepend-inner-icon="mdi-folder-multiple"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">Member Type</h6>
                    <v-text-field
                      :value="MemberTypeName"
                      placeholder="Shown after selecting Contribution Type"
                      readonly
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-group"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">
                      <span class="text-danger">*</span> Payment Category
                    </h6>
                    <v-select
                      :items="PaymentCategoryOptions"
                      v-model="PaymentCategory"
                      required
                      dense
                      outlined
                      prepend-inner-icon="mdi-tab"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">Active Status</h6>
                    <v-switch
                      v-model="ActiveStatus"
                      :label="`${ActiveStatus ? 'Active' : 'Inactive'}`"
                      class="mt-0"
                      inset
                    ></v-switch>
                  </v-col>
                </v-row>
                <div class="px-4 pb-4">
                  <v-alert dense text :type="CategoryInstruction.type" class="mb-2">
                    <small>{{ CategoryInstruction.text }}</small>
                  </v-alert>
                  <v-alert dense text :type="ActiveInstruction.type" class="mb-0">
                    <small>{{ ActiveInstruction.text }}</small>
                  </v-alert>
                </div>
              </v-card>

              <!-- 2. Payment window -->
              <v-card outlined class="st-section mb-4">
                <div class="st-section-title">
                  <v-avatar size="26" color="#0a79df" class="mr-2 white--text">2</v-avatar>
                  Payment window
                </div>
                <v-row class="px-4 pt-2">
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">
                      <span class="text-danger">*</span> Start Date
                    </h6>
                    <v-menu
                      v-model="menu1"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="StartDate"
                          :rules="StartDateRules"
                          placeholder="Select Start Date"
                          readonly
                          outlined
                          dense
                          prepend-inner-icon="mdi-calendar-start"
                          v-bind="attrs"
                          v-on="on"
                          required
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="StartDate"
                        @input="menu1 = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">
                      <span class="text-danger">*</span> End Date
                    </h6>
                    <v-menu
                      v-model="menu2"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="EndDate"
                          :rules="EndDateRules"
                          placeholder="Select End Date"
                          readonly
                          outlined
                          dense
                          prepend-inner-icon="mdi-calendar-end"
                          v-bind="attrs"
                          v-on="on"
                          required
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="EndDate"
                        :min="StartDate"
                        @input="menu2 = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">
                      <span class="text-danger">*</span> No. of Times
                    </h6>
                    <v-text-field
                      v-model="NoOfTimes"
                      :rules="NoOfTimesRules"
                      placeholder="Enter Number of Times"
                      type="number"
                      min="0"
                      required
                      dense
                      outlined
                      prepend-inner-icon="mdi-counter"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">Voting Permission</h6>
                    <v-switch
                      v-model="VotingFlag"
                      :label="`${VotingFlag ? 'Yes' : 'No'}`"
                      class="mt-0"
                      inset
                    ></v-switch>
                  </v-col>
                </v-row>
                <div class="px-4 pb-4">
                  <v-alert dense text :type="WindowInstruction.type" class="mb-2">
                    <small>{{ WindowInstruction.text }}</small>
                  </v-alert>
                  <v-alert dense text :type="VotingInstruction.type" class="mb-0">
                    <small>{{ VotingInstruction.text }}</small>
                  </v-alert>
                </div>
              </v-card>

              <!-- 3. Amount -->
              <v-card outlined class="st-section mb-4">
                <div class="st-section-title">
                  <v-avatar size="26" color="#0a79df" class="mr-2 white--text">3</v-avatar>
                  Amount (per member)
                </div>
                <v-row class="px-4 pt-2">
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">
                      <span class="text-danger">*</span> Amount
                    </h6>
                    <v-text-field
                      v-model="Amount"
                      :rules="AmountRules"
                      placeholder="Enter Amount"
                      type="number"
                      min="0"
                      required
                      dense
                      outlined
                      prepend-inner-icon="mdi-currency-inr"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">Welfare Amount</h6>
                    <v-text-field
                      v-model="WelfareAmount"
                      :rules="WelfareAmountRules"
                      placeholder="Enter Welfare Amount"
                      type="number"
                      min="0"
                      dense
                      outlined
                      prepend-inner-icon="mdi-hand-heart"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">
                      <span class="text-danger">*</span> Tax Percentage
                    </h6>
                    <v-text-field
                      v-model="TaxPercentage"
                      :rules="TaxPercentageRules"
                      placeholder="Enter Tax Percentage"
                      type="number"
                      min="0"
                      required
                      dense
                      outlined
                      prepend-inner-icon="mdi-percent"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <div class="px-4 pb-4">
                  <v-alert dense text :type="AmountInstruction.type" class="mb-0">
                    <small>{{ AmountInstruction.text }}</small>
                  </v-alert>
                </div>
              </v-card>

              <!-- 4. Penalty -->
              <v-card outlined class="st-section mb-4">
                <div class="st-section-title">
                  <v-avatar size="26" color="#0a79df" class="mr-2 white--text">4</v-avatar>
                  Penalty (late fee)
                </div>
                <v-row class="px-4 pt-2">
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">Penalty</h6>
                    <v-switch
                      v-model="FineFlag"
                      :label="`${FineFlag ? 'Yes' : 'No'}`"
                      class="mt-0"
                      inset
                    ></v-switch>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">
                      Is Payment Allowed After Last Date?
                    </h6>
                    <v-switch
                      v-model="DeadlineFlag"
                      :label="`${DeadlineFlag ? 'Yes' : 'No'}`"
                      class="mt-0"
                      inset
                    ></v-switch>
                  </v-col>
                  <template v-if="FineFlag">
                    <v-col cols="12" sm="6">
                      <h6 class="mb-2 font-weight-bold">
                        <span class="text-danger">*</span> Fine Amount
                      </h6>
                      <v-text-field
                        v-model="FineAmount"
                        :rules="FineAmountRules"
                        placeholder="Enter Fine Amount"
                        type="number"
                        min="0"
                        required
                        dense
                        outlined
                        prepend-inner-icon="mdi-currency-inr"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <h6 class="mb-2 font-weight-bold">
                        <span class="text-danger">*</span> Penalty End Date
                      </h6>
                      <v-menu
                        v-model="menu3"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="FineEndDate"
                            :rules="FineEndDateRules"
                            placeholder="Select Penalty End Date"
                            readonly
                            outlined
                            dense
                            prepend-inner-icon="mdi-calendar-alert"
                            v-bind="attrs"
                            v-on="on"
                            required
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="FineEndDate"
                          :min="EndDate"
                          @input="menu3 = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                  </template>
                </v-row>
                <div class="px-4 pb-4">
                  <v-alert dense text :type="PenaltyInstruction.type" class="mb-2">
                    <small>{{ PenaltyInstruction.text }}</small>
                  </v-alert>
                  <v-alert dense text :type="DeadlineInstruction.type" class="mb-0">
                    <small>{{ DeadlineInstruction.text }}</small>
                  </v-alert>
                </div>
              </v-card>

              <!-- 5. Advance payment -->
              <v-card outlined class="st-section mb-2">
                <div class="st-section-title">
                  <v-avatar size="26" color="#0a79df" class="mr-2 white--text">5</v-avatar>
                  Advance payment
                </div>
                <v-row class="px-4 pt-2">
                  <v-col cols="12" sm="6">
                    <h6 class="mb-2 font-weight-bold">Advance Payment</h6>
                    <v-switch
                      v-model="PaymentFlag"
                      :label="`${PaymentFlag ? 'Yes' : 'No'}`"
                      class="mt-0"
                      inset
                    ></v-switch>
                  </v-col>
                  <v-col cols="12" sm="6" v-if="PaymentFlag">
                    <h6 class="mb-2 font-weight-bold">
                      <span class="text-danger">*</span> Balance Amount
                    </h6>
                    <v-text-field
                      v-model="BalanceAmount"
                      :rules="BalanceAmountRules"
                      placeholder="Enter Balance Amount"
                      type="number"
                      min="0"
                      required
                      dense
                      outlined
                      prepend-inner-icon="mdi-currency-inr"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <div class="px-4 pb-4">
                  <v-alert dense text :type="AdvanceInstruction.type" class="mb-0">
                    <small>{{ AdvanceInstruction.text }}</small>
                  </v-alert>
                </div>
              </v-card>
            </v-col>

            <!-- RIGHT: Summary -->
            <v-col cols="12" md="4">
              <div class="st-summary-sticky">
                <v-card outlined class="st-summary mb-4">
                  <div class="st-summary-title">
                    <v-icon small color="white" class="mr-2">mdi-calculator</v-icon>
                    Per member summary
                  </div>
                  <v-simple-table dense class="st-summary-table">
                    <tbody>
                      <tr v-if="toNumber(WelfareAmount) > 0">
                        <td>Welfare Amount</td>
                        <td class="text-right">{{ formatAmount(WelfareAmount) }}</td>
                      </tr>
                      <tr>
                        <td>Amount{{ toNumber(WelfareAmount) > 0 ? " + Welfare" : "" }}</td>
                        <td class="text-right">{{ formatAmount(FeeAmountValue) }}</td>
                      </tr>
                      <tr>
                        <td>Tax ({{ TaxValue }}%)</td>
                        <td class="text-right">{{ formatAmount(TaxAmountValue) }}</td>
                      </tr>
                      <tr class="st-total-row">
                        <td>Final Amount</td>
                        <td class="text-right">{{ formatAmount(TotalAmountValue) }}</td>
                      </tr>
                      <tr v-if="FineFlag">
                        <td>Penalty incl. tax</td>
                        <td class="text-right">+ {{ formatAmount(FineTotalValue) }}</td>
                      </tr>
                      <tr v-if="FineFlag" class="st-total-row">
                        <td>With penalty</td>
                        <td class="text-right">{{ formatAmount(PenaltyTotalValue) }}</td>
                      </tr>
                      <tr v-if="PaymentFlag">
                        <td>Balance due later incl. tax</td>
                        <td class="text-right">{{ formatAmount(BalanceTotalValue) }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>

                <v-card outlined class="st-summary">
                  <div class="st-summary-title">
                    <v-icon small color="white" class="mr-2">mdi-timeline-clock</v-icon>
                    Payment timeline
                  </div>
                  <v-timeline dense align-top class="pr-3 py-2">
                    <v-timeline-item
                      v-for="(t, i) in TimelineItems"
                      :key="i"
                      :color="t.color"
                      small
                    >
                      <div class="text-caption font-weight-bold">{{ t.title }}</div>
                      <div class="text-caption">{{ t.text }}</div>
                    </v-timeline-item>
                  </v-timeline>
                </v-card>

                <div class="d-flex flex-wrap mt-4">
                  <v-chip small class="mr-2 mb-2" :color="PaymentCategoryColor" dark>
                    {{ PaymentCategoryTxt }}
                  </v-chip>
                  <v-chip small class="mr-2 mb-2" :color="PaymentFlag ? 'deep-purple' : 'grey'" dark>
                    {{ PaymentFlag ? "Advance" : "Full payment" }}
                  </v-chip>
                  <v-chip small class="mr-2 mb-2" :color="ActiveStatus ? 'green' : 'red'" dark>
                    {{ ActiveStatus ? "Active" : "Inactive" }}
                  </v-chip>
                  <v-chip small class="mr-2 mb-2" :color="VotingFlag ? 'green' : 'grey'" dark>
                    {{ VotingFlag ? "Voting" : "No voting" }}
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Action Buttons -->
          <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                :disabled="!valid1"
                @click.prevent="confirmAlert"
                :loading="SubmitFlag"
                elevation="12"
                rounded
                large
                color="#1db954"
                class="font-size-h6 px-8 mr-3 white--text"
              >
                <v-icon left>mdi-check-circle</v-icon>
                Save
              </v-btn>
              <v-btn
                @click="closePrompt"
                elevation="12"
                rounded
                large
                color="#757575"
                class="font-size-h6 px-8 white--text"
              >
                <v-icon left>mdi-close-circle</v-icon>
                Close
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  props: {
    editRecordPrompt: {
      type: Boolean,
      required: true,
    },
    pageTitle: {
      type: String,
    },
    pageDescription: {
      type: String,
    },
    recordIndex: {
      required: true,
    },
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,
      OverlayLoadingFlag: false,

      rows: {},

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCode: "",
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      ContributionTypeIdRules: [(v) => !!v || "Contribution Type is required"],
      ContributionTypeId: "",
      ContributionTypeIdOptions: [],
      ContributionTypeIdOptionsLoading: false,

      MemberTypeName: "",

      VotingFlag: 0,
      VotingFlagRules: [(v) => !!v || "Voting Permission is required"],

      StartDate: "",
      menu1: false,

      EndDate: "",
      menu2: false,
      Amount: "",
      AmountRules: [
        (v) => (v !== "" && v !== null && v !== undefined) || "Amount is required",
        (v) => parseFloat(v) >= 0 || "Amount should be 0 or above",
      ],

      WelfareAmount: "",
      WelfareAmountRules: [
        (v) => v === "" || v === null || v === undefined || parseFloat(v) >= 0 || "Welfare Amount should be 0 or above",
      ],

      BalanceAmount: "",
      BalanceAmountRules: [
        (v) => (v !== "" && v !== null && v !== undefined) || "Balance Amount is required",
        (v) => parseFloat(v) > 0 || "Balance Amount should be above 0",
      ],

      PaymentCategory: 1,
      PaymentCategoryOptions: [
        { value: 1, text: "Normal" },
        { value: 2, text: "Renewal" },
        { value: 3, text: "New Member" },
      ],

      TaxPercentage: "",
      TaxPercentageRules: [
        (v) => (v !== "" && v !== null && v !== undefined) || "Tax Percentage is required",
        (v) => parseFloat(v) >= 0 || "Tax Percentage should be 0 or above",
      ],

      FinalAmount: 0,

      FineFlag: 0,
      FineFlagRules: [(v) => !!v || "Fine is required"],

      DeadlineFlag: 0,
      DeadlineFlagRules: [(v) => !!v || "Is allow deadline is required"],

      PaymentFlag: 0,
      PaymentFlagRules: [(v) => !!v || "Is allow payment is required"],

      ActiveStatus: 0,
      ActiveStatusRules: [(v) => !!v || "Active Status is required"],

      FineAmount: "",
      FineAmountRules: [
        (v) => (v !== "" && v !== null && v !== undefined) || "Fine Amount is required",
        (v) => parseFloat(v) > 0 || "Fine Amount should be above 0",
      ],

      FineEndDate: "",
      menu3: false,

      NoOfTimes: "",
      NoOfTimesRules: [
        (v) => (v !== "" && v !== null && v !== undefined) || "No of times is required",
        (v) => parseFloat(v) >= 0 || "No of times should be 0 or above",
      ],
    };
  },
  computed: {
    StartDateRules() {
      return [(v) => !!v || "Start Date is required"];
    },
    EndDateRules() {
      return [
        (v) => !!v || "End Date is required",
        (v) =>
          !v || !this.StartDate || v >= this.StartDate ||
          "End Date should be on or after Start Date",
      ];
    },
    FineEndDateRules() {
      return [
        (v) => !!v || "Penalty End Date is required",
        (v) =>
          !v || !this.EndDate || v >= this.EndDate ||
          "Penalty End Date should be on or after End Date",
      ];
    },
    FeeAmountValue() {
      return this.toNumber(this.Amount) + this.toNumber(this.WelfareAmount);
    },
    TaxValue() {
      return this.toNumber(this.TaxPercentage);
    },
    TaxAmountValue() {
      return this.round2((this.FeeAmountValue * this.TaxValue) / 100);
    },
    TotalAmountValue() {
      return this.round2(this.FeeAmountValue + this.TaxAmountValue);
    },
    FineAmountValue() {
      return this.FineFlag ? this.toNumber(this.FineAmount) : 0;
    },
    FineTotalValue() {
      return this.round2(
        this.FineAmountValue + (this.FineAmountValue * this.TaxValue) / 100
      );
    },
    PenaltyTotalValue() {
      return this.round2(this.TotalAmountValue + this.FineTotalValue);
    },
    BalanceAmountValue() {
      return this.PaymentFlag ? this.toNumber(this.BalanceAmount) : 0;
    },
    BalanceTotalValue() {
      return this.round2(
        this.BalanceAmountValue + (this.BalanceAmountValue * this.TaxValue) / 100
      );
    },
    LastDate() {
      return this.FineFlag && this.FineEndDate ? this.FineEndDate : this.EndDate;
    },
    PaymentCategoryTxt() {
      var item = this.PaymentCategoryOptions.find(
        (e) => e.value == this.PaymentCategory
      );
      return item ? item.text : "Normal";
    },
    PaymentCategoryColor() {
      return this.PaymentCategory == 3
        ? "orange darken-2"
        : this.PaymentCategory == 2
        ? "teal"
        : "blue";
    },
    CategoryInstruction() {
      if (this.PaymentCategory == 2) {
        return {
          type: "info",
          text: "Renewal: on the assign page the LO sees only Previous Half and Existing tabs. Adding new members is blocked for this fee.",
        };
      }
      if (this.PaymentCategory == 3) {
        return {
          type: "info",
          text: "New Member: on the assign page the LO sees only the Add New Members tab. Previous Half and Existing members are blocked for this fee.",
        };
      }
      return {
        type: "info",
        text: "Normal: on the assign page the LO sees all tabs - Previous Half, Existing and Add New Members.",
      };
    },
    WindowInstruction() {
      if (!this.StartDate || !this.EndDate) {
        return {
          type: "warning",
          text: "Select Start Date and End Date. LOs can pay only inside this window without penalty.",
        };
      }
      if (this.EndDate < this.StartDate) {
        return {
          type: "error",
          text: "End Date is before Start Date. Kindly correct the dates.",
        };
      }
      return {
        type: "info",
        text: `LOs can pay this fee from ${this.formatDate(this.StartDate)} to ${this.formatDate(this.EndDate)} at ${this.formatAmount(this.TotalAmountValue)} per member. Before ${this.formatDate(this.StartDate)} the fee is not shown.`,
      };
    },
    VotingInstruction() {
      return this.VotingFlag
        ? { type: "success", text: "Voting Permission is on: members paid under this fee get voting rights." }
        : { type: "info", text: "Voting Permission is off: paying this fee does not give voting rights." };
    },
    ActiveInstruction() {
      return this.ActiveStatus
        ? { type: "success", text: "Active: LOs can see and pay this fee within its payment window." }
        : { type: "error", text: "Inactive: this fee is hidden from the payment page. LOs cannot pay it." };
    },
    AmountInstruction() {
      if (this.Amount === "" || this.Amount === null) {
        return { type: "warning", text: "Enter the Amount and Tax Percentage to see the per member total." };
      }
      var welfare_txt = this.toNumber(this.WelfareAmount) > 0
        ? `${this.formatAmount(this.Amount)} + welfare ${this.formatAmount(this.WelfareAmount)} = ${this.formatAmount(this.FeeAmountValue)}, `
        : "";
      return {
        type: "info",
        text: `Each member costs ${welfare_txt}${this.formatAmount(this.FeeAmountValue)} + ${this.TaxValue}% tax (${this.formatAmount(this.TaxAmountValue)}) = ${this.formatAmount(this.TotalAmountValue)}. The LO pays this amount x number of members selected.`,
      };
    },
    PenaltyInstruction() {
      if (!this.FineFlag) {
        return {
          type: "info",
          text: `Penalty is off: no late fee. The amount stays ${this.formatAmount(this.TotalAmountValue)} per member.`,
        };
      }
      if (!this.FineEndDate || !this.EndDate) {
        return {
          type: "warning",
          text: "Penalty is on: enter the Fine Amount and select a Penalty End Date on or after the End Date.",
        };
      }
      return {
        type: "warning",
        text: `Penalty is on: after ${this.formatDate(this.EndDate)} until ${this.formatDate(this.FineEndDate)}, ${this.formatAmount(this.FineAmountValue)} + ${this.TaxValue}% tax = ${this.formatAmount(this.FineTotalValue)} extra is added, so each member costs ${this.formatAmount(this.PenaltyTotalValue)}.`,
      };
    },
    DeadlineInstruction() {
      var last_date = this.LastDate ? this.formatDate(this.LastDate) : "the last date";
      if (this.DeadlineFlag) {
        var amount_txt = this.FineFlag
          ? `with penalty (${this.formatAmount(this.PenaltyTotalValue)} per member)`
          : `at ${this.formatAmount(this.TotalAmountValue)} per member`;
        return {
          type: "success",
          text: `Payment allowed after last date: LOs can still pay after ${last_date} ${amount_txt}. The fee never closes.`,
        };
      }
      return {
        type: "error",
        text: `Payment not allowed after last date: the fee closes after ${last_date}. LOs cannot pay it after that.`,
      };
    },
    AdvanceInstruction() {
      if (!this.PaymentFlag) {
        return {
          type: "info",
          text: `Full payment: ${this.formatAmount(this.TotalAmountValue)} per member clears this fee. No balance is collected later (Balance Amount is saved as 0). Use this for normal subscriptions.`,
        };
      }
      if (this.BalanceAmountValue <= 0) {
        return {
          type: "warning",
          text: "Advance payment is on: enter the Balance Amount that the LO must pay later for each member.",
        };
      }
      return {
        type: "warning",
        text: `Advance payment: the LO pays ${this.formatAmount(this.TotalAmountValue)} per member now. A balance of ${this.formatAmount(this.BalanceAmountValue)} + ${this.TaxValue}% tax = ${this.formatAmount(this.BalanceTotalValue)} per member stays due and must be paid later from the balance payment list. Until then those members show as balance not paid.`,
      };
    },
    TimelineItems() {
      var items = [];
      items.push({
        color: "blue",
        title: this.StartDate ? this.formatDate(this.StartDate) : "Start Date",
        text: "Payment opens",
      });
      items.push({
        color: "green",
        title: this.EndDate ? this.formatDate(this.EndDate) : "End Date",
        text: `Last date without penalty - ${this.formatAmount(this.TotalAmountValue)}`,
      });
      if (this.FineFlag) {
        items.push({
          color: "orange",
          title: this.FineEndDate ? this.formatDate(this.FineEndDate) : "Penalty End Date",
          text: `Last date with penalty - ${this.formatAmount(this.PenaltyTotalValue)}`,
        });
      }
      items.push({
        color: this.DeadlineFlag ? "teal" : "red",
        title: "After that",
        text: this.DeadlineFlag ? "Payment still allowed" : "Payment closed",
      });
      return items;
    },
  },
  watch: {
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.JciYearCodeOptionsLoading = false;
    },
    ContributionTypeIdOptions: function () {
      console.log("watch ContributionTypeIdOptions");
      this.ContributionTypeIdOptionsLoading = false;
      this.getMemberType();
    },
    ContributionTypeId: function () {
      console.log("watch ContributionTypeId");
      this.getMemberType();
    },
    Amount: function () {
      console.log("watch Amount changed");
      this.calculateTotal();
    },
    TaxPercentage: function () {
      console.log("watch TaxPercentage changed");
      this.calculateTotal();
    },
    rows: {
      handler: function (newVal) {
        console.log("watch rows handler");
        var tr = newVal;
        var n1 = Object.keys(tr).length;
        console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
        if (n1 > 0) {
          this.JciYearCode = tr.YearId;
          this.ContributionTypeId = tr.ContributionTypeId;
          this.StartDate = tr.StartDate;
          this.EndDate = tr.EndDate;
          this.Amount = tr.ContributionAmount;
          this.WelfareAmount = tr.WelfareAmount;
          this.TaxPercentage = tr.Tax;
          this.NoOfTimes = tr.ContributionCount;
          this.VotingFlag = tr.VotingFlag;
          this.FineFlag = tr.FineFlag;
          this.FineAmount = tr.FineAmount;
          this.FineEndDate = tr.FineEndDate;
          this.DeadlineFlag = tr.DeadlineFlag;
          this.PaymentFlag = tr.PaymentFlag;
          this.ActiveStatus = tr.ActiveStatus;
          this.BalanceAmount = tr.BalanceAmount;
          this.PaymentCategory = tr.PaymentCategory ? tr.PaymentCategory : 1;
          this.getMemberType();
          // Call calculateTotal with a delay to ensure data is set
          this.$nextTick(() => {
            this.calculateTotal();
          });
        }
      },
      deep: true,
    },
  },
  created() {
    // this.initialize()
  },
  methods: {
    toNumber(value) {
      var number = parseFloat(value);
      return isNaN(number) || number < 0 ? 0 : number;
    },
    round2(value) {
      return Math.round(value * 100) / 100;
    },
    formatAmount(value) {
      var number = this.toNumber(value);
      return (
        "₹" +
        number.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      );
    },
    formatDate(value) {
      if (!value) {
        return "";
      }
      var date = new Date(value + "T00:00:00");
      if (isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    validateForm() {
      console.log("validateForm called");
      this.$refs.form1.validate();
    },
    resetForm() {
      console.log("resetForm called");
      this.$refs.form1.reset();
      //   this.$refs.ProfilePicture.reset();
    },
    resetValidation() {
      console.log("resetValidation called");
      this.$refs.form1.resetValidation();
    },
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideEditRecordPrompt", this.ResultFlag);
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var recordIndex = this.recordIndex;
      console.log("recordIndex=" + recordIndex);
      this.getRecordDetails();
      this.getJciYearCodeOptions();
      this.getContributionTypeIdOptions();
      this.LoadingFlag = false;
    },
    getJciYearCodeOptions() {
      this.LoadingFlag = true;
      this.JciYearCodeOptionsLoading = true;
      console.log("getJciYearCodeOptions called");
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getContributionTypeIdOptions() {
      this.LoadingFlag = true;
      this.ContributionTypeIdOptionsLoading = true;
      console.log("ContributionTypeIdOptions called");
      var selectbox1_source = "ContributionTypeId";
      var selectbox1_destination = "ContributionTypeIdOptions";
      var selectbox1_url = "api/contribution-type/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    calculateTotal() {
      console.log("calculateTotal called");
      this.FinalAmount = this.TotalAmountValue;
    },
    getMemberType() {
      console.log("getMemberType called");
      var searchValue1 = this.ContributionTypeId;
      var index1 = this.ContributionTypeIdOptions.map((e) => e.value).indexOf(
        searchValue1
      );
      console.log("searchValue1=" + searchValue1 + ", index1=" + index1);
      var MemberTypeName =
        index1 >= 0 ? this.ContributionTypeIdOptions[index1].MemberTypeName : "";
      console.log("MemberTypeName=" + MemberTypeName);
      this.MemberTypeName = MemberTypeName;
    },
    confirmAlert() {
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);
      if (validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        this.toast("error", "Kindly fill the required fields");
      }
    },
    getRecordDetails() {
      // add todo in store and clear all fields in dialog
      console.log("getRecordDetails called");

      //   this.resetMessageTxt();

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      console.log("server_url=" + server_url + ", token=" + token);

      var recordIndex = this.recordIndex;

      console.log("recordIndex=" + recordIndex);

      if (recordIndex != "" && recordIndex != (null || undefined)) {
        var upload = {
          UserInterface: 1,
          YearwiseContributionId: recordIndex,
        };
        console.log("upload=" + JSON.stringify(upload));

        const thisIns = this;
        var output = "";
        var flag = 0;
        var records = [];

        // thisIns.pageLoadingOn()
        thisIns.OverlayLoadingFlag = true;
        thisIns.ProgessStart = 1;
        thisIns.valid = false;

        var CancelToken = this.CancelToken;
        var cancel;

        var add_url = server_url + "api/year-wise-contribution/show";

        console.log("add_url=" + add_url);

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
          cancelToken: new CancelToken(function executor(c) {
            // An executor function receives a cancel function as a parameter
            cancel = c;
          }),
        })
          .then(function (response) {
            // thisIns.pageLoadingOff()
            thisIns.OverlayLoadingFlag = false;
            thisIns.ProgessStart = 0;
            thisIns.valid = true;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.rows = records;
              thisIns.toast("success", output);
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            thisIns.OverlayLoadingFlag = false;
            thisIns.ProgessStart = 0;
            thisIns.valid = true;
            console.log({ error });
          });
      } else {
        console.log("some errors occured");
        var message = "Year wise contribution id should not be empty";
        this.toast("error", message);
      }
    },
    submitForm() {
      console.log("submitForm is called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      //   validate1 = false;

      if (validate1) {
        this.SubmitFlag = true;
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-contribution/update";
        // Penalty off -> no fine, fine end date falls back to End Date in backend
        // Advance off -> no balance to collect
        var upload = {
          YearwiseContributionId: this.recordIndex,
          YearId: this.JciYearCode,
          ContributionTypeId: this.ContributionTypeId,
          StartDate: this.StartDate,
          EndDate: this.EndDate,
          Amount: this.Amount,
          WelfareAmount: this.WelfareAmount,
          TaxPercentage: this.TaxPercentage,
          FineFlag: this.FineFlag ? 1 : 0,
          DeadlineFlag: this.DeadlineFlag ? 1 : 0,
          PaymentFlag: this.PaymentFlag ? 1 : 0,
          FineAmount: this.FineFlag ? this.FineAmount : 0,
          FineEndDate: this.FineFlag ? this.FineEndDate : "",
          VotingFlag: this.VotingFlag ? 1 : 0,
          NoOfTimes: this.NoOfTimes,
          ActiveStatus: this.ActiveStatus ? 1 : 0,
          BalanceAmount: this.PaymentFlag ? this.BalanceAmount : 0,
          PaymentCategory: this.PaymentCategory,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SubmitFlag = false;
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.ResultFlag = true;
              thisIns.sweetAlert("success", output, true);
              thisIns.resetForm();
              thisIns.closePrompt();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields";
        }
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
.page-header-gradient {
  background: linear-gradient(90deg, #004e92, #0a79df);
  border-radius: 10px 10px 0 0;
}

.st-header-sub {
  opacity: 0.85;
}

.st-card {
  border-radius: 12px;
  max-height: 90vh;
  overflow-y: auto;
}

.st-card::-webkit-scrollbar {
  width: 8px;
}

.st-card::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.st-card::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.st-section {
  border-radius: 10px !important;
  border-left: 4px solid #0a79df !important;
}

.st-section-title {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 15px;
  color: #004e92;
  padding: 12px 16px 0 16px;
}

.st-summary-sticky {
  position: sticky;
  top: 0;
}

.st-summary {
  border-radius: 10px !important;
  overflow: hidden;
}

.st-summary-title {
  display: flex;
  align-items: center;
  background: linear-gradient(90deg, #004e92, #0a79df);
  color: white;
  font-weight: 700;
  font-size: 14px;
  padding: 8px 12px;
}

.st-summary-table td {
  font-size: 13px !important;
}

.st-total-row td {
  font-weight: 700;
  color: #004e92;
}

.text-danger {
  color: #f44336;
  font-weight: bold;
}

h6 {
  font-size: 14px;
  letter-spacing: 0.5px;
  color: #333;
}

.rounded-xl {
  border-radius: 12px;
}
</style>
